import { useEffect, useMemo, useState } from "react";
import {
  ArrowsClockwise,
  Buildings,
  CaretDown,
  CheckCircle,
  Plus,
  ShieldCheck,
  UserPlus,
  UsersThree,
  WarningCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  assignCenterAdministrator,
  createPlatformCenter,
  listCenterAdministrators,
  listPlatformAudit,
  listPlatformCenters,
} from "@/lib/platform";
import { TIMEZONE_OPTIONS } from "@/lib/admin/organizations";

const EMPTY_CENTER = {
  name: "",
  slug: "",
  timezone: "America/Chicago",
  display_name: "",
  administrator_email: "",
  administrator_name: "",
};

export default function PlatformControlCenter() {
  const { platformRole } = useAuth();
  const isOwner = platformRole === "platform_owner";
  const [centers, setCenters] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CENTER);
  const [creating, setCreating] = useState(false);
  const [openCenter, setOpenCenter] = useState(null);
  const [administrators, setAdministrators] = useState({});
  const [adminLoading, setAdminLoading] = useState({});
  const [adminForms, setAdminForms] = useState({});
  const [adminSaving, setAdminSaving] = useState({});

  const reload = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [nextCenters, nextAudit] = await Promise.all([
        listPlatformCenters(),
        listPlatformAudit(),
      ]);
      setCenters(nextCenters || []);
      setAudit(nextAudit || []);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const totalAdministrators = useMemo(
    () => centers.reduce((sum, center) => sum + (center.administrator_count || 0), 0),
    [centers],
  );

  const normalizeSlug = (value) => value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

  const onCreate = async (event) => {
    event.preventDefault();
    setCreating(true);
    try {
      const created = await createPlatformCenter({
        ...createForm,
        name: createForm.name.trim(),
        slug: normalizeSlug(createForm.slug),
        display_name: createForm.display_name.trim() || null,
        administrator_email: createForm.administrator_email.trim() || null,
        administrator_name: createForm.administrator_name.trim() || null,
      });
      setCenters((previous) => [...previous, created]);
      setAudit((previous) => [{ action: "center_created", organization_id: created.id, created_at: new Date().toISOString(), id: `local-${created.id}`, metadata: { slug: created.slug } }, ...previous]);
      setCreateForm(EMPTY_CENTER);
      setShowCreate(false);
      toast.success("Center created");
    } catch (error) {
      toast.error(error.message || "Could not create center");
    } finally {
      setCreating(false);
    }
  };

  const toggleAdministrators = async (centerId) => {
    if (openCenter === centerId) {
      setOpenCenter(null);
      return;
    }
    setOpenCenter(centerId);
    if (administrators[centerId]) return;
    setAdminLoading((previous) => ({ ...previous, [centerId]: true }));
    try {
      const rows = await listCenterAdministrators(centerId);
      setAdministrators((previous) => ({ ...previous, [centerId]: rows || [] }));
    } catch (error) {
      toast.error(error.message || "Could not load center administrators");
    } finally {
      setAdminLoading((previous) => ({ ...previous, [centerId]: false }));
    }
  };

  const assignAdministrator = async (event, centerId) => {
    event.preventDefault();
    const form = adminForms[centerId] || {};
    setAdminSaving((previous) => ({ ...previous, [centerId]: true }));
    try {
      const assigned = await assignCenterAdministrator(centerId, {
        email: form.email?.trim(),
        full_name: form.full_name?.trim() || null,
      });
      const alreadyAssigned = (administrators[centerId] || []).some(
        (administrator) => administrator.profile_id === assigned.profile_id,
      );
      if (!alreadyAssigned) {
        setAdministrators((previous) => ({
          ...previous,
          [centerId]: [...(previous[centerId] || []), assigned],
        }));
        setCenters((previous) => previous.map((center) => (
          center.id === centerId
            ? { ...center, administrator_count: (center.administrator_count || 0) + 1 }
            : center
        )));
      }
      setAdminForms((previous) => ({ ...previous, [centerId]: { email: "", full_name: "" } }));
      toast.success(assigned.invite_email_sent ? "Administrator invited" : "Administrator assigned");
    } catch (error) {
      toast.error(error.message || "Could not assign administrator");
    } finally {
      setAdminSaving((previous) => ({ ...previous, [centerId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="h-9 w-9 animate-spin rounded-full border-2 border-primary/30 border-t-primary" />
      </div>
    );
  }

  if (loadError) {
    return <PlatformLoadError onRetry={reload} />;
  }

  return (
    <div data-testid="platform-control-center" className="space-y-8">
      <section className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">Control plane</p>
          <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
            Centers, not family data
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-neutral-600">
            Set up centers and their administrators from one shared HoneyAcorn platform. This workspace does not surface children, families, health records, messages, or payment data.
          </p>
        </div>
        {isOwner ? (
          <button
            type="button"
            data-testid="platform-create-center"
            onClick={() => setShowCreate((open) => !open)}
            className="inline-flex items-center gap-2 rounded-full bg-neutral-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-neutral-800"
          >
            <Plus size={17} weight="bold" />
            Add center
          </button>
        ) : null}
      </section>

      <section className="grid gap-4 sm:grid-cols-3">
        <Metric label="Centers" value={centers.length} icon={<Buildings size={18} weight="duotone" />} />
        <Metric label="Center administrators" value={totalAdministrators} icon={<UsersThree size={18} weight="duotone" />} />
        <Metric label="Platform access" value={isOwner ? "Owner" : "Admin"} icon={<ShieldCheck size={18} weight="duotone" />} />
      </section>

      {showCreate && isOwner ? (
        <section className="rounded-3xl border border-neutral-200 bg-white p-5 shadow-card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">New center</p>
              <h2 className="mt-1 font-heading text-xl font-semibold tracking-tight text-neutral-900">Create a clean center workspace</h2>
              <p className="mt-1 text-xs leading-5 text-neutral-500">Module defaults are configured without creating a code fork or any HoneyAcorn price.</p>
            </div>
            <button type="button" onClick={() => setShowCreate(false)} className="text-sm font-semibold text-neutral-500 hover:text-neutral-900">Cancel</button>
          </div>
          <form className="mt-5 grid gap-4 md:grid-cols-2" onSubmit={onCreate}>
            <TextField label="Center name" value={createForm.name} required onChange={(name) => setCreateForm((form) => ({ ...form, name, slug: form.slug || normalizeSlug(name) }))} />
            <TextField label="Center URL key" value={createForm.slug} required hint="Lowercase letters, numbers, and hyphens." onChange={(slug) => setCreateForm((form) => ({ ...form, slug: normalizeSlug(slug) }))} />
            <TextField label="Parent-facing name" value={createForm.display_name} onChange={(display_name) => setCreateForm((form) => ({ ...form, display_name }))} />
            <label className="block">
              <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">Timezone</span>
              <select value={createForm.timezone} onChange={(event) => setCreateForm((form) => ({ ...form, timezone: event.target.value }))} className={inputClass}>
                {TIMEZONE_OPTIONS.map((timezone) => <option key={timezone} value={timezone}>{timezone}</option>)}
              </select>
            </label>
            <div className="border-t border-neutral-100 pt-4 md:col-span-2">
              <p className="text-sm font-semibold text-neutral-900">First center administrator <span className="font-normal text-neutral-400">(optional)</span></p>
              <p className="mt-1 text-xs text-neutral-500">Assign an existing account or send a normal HoneyAcorn invitation.</p>
            </div>
            <TextField label="Administrator email" type="email" value={createForm.administrator_email} onChange={(administrator_email) => setCreateForm((form) => ({ ...form, administrator_email }))} />
            <TextField label="Administrator name" value={createForm.administrator_name} onChange={(administrator_name) => setCreateForm((form) => ({ ...form, administrator_name }))} />
            <div className="flex justify-end md:col-span-2">
              <button disabled={creating} className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-hover disabled:cursor-wait disabled:opacity-60">
                {creating ? "Creating…" : "Create center"}
              </button>
            </div>
          </form>
        </section>
      ) : null}

      <section className="rounded-3xl border border-neutral-200 bg-white shadow-card">
        <div className="flex items-center justify-between gap-4 border-b border-neutral-100 px-5 py-4">
          <div>
            <h2 className="font-heading text-xl font-semibold tracking-tight text-neutral-900">Centers</h2>
            <p className="mt-1 text-xs text-neutral-500">Center metadata and administrator setup only.</p>
          </div>
          <span className="rounded-full bg-neutral-100 px-3 py-1 text-xs font-semibold text-neutral-600">{centers.length}</span>
        </div>
        {centers.length === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-neutral-500">No centers are configured yet.</div>
        ) : (
          <ul className="divide-y divide-neutral-100">
            {centers.map((center) => {
              const expanded = openCenter === center.id;
              const rows = administrators[center.id] || [];
              const form = adminForms[center.id] || { email: "", full_name: "" };
              return (
                <li key={center.id} className="px-5 py-4">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-heading text-base font-semibold text-neutral-900">{center.display_name || center.name}</p>
                      <p className="mt-1 truncate font-mono text-[11px] text-neutral-500">/{center.slug} · {center.timezone}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-[#F8F7F4] px-2.5 py-1 text-xs font-semibold text-neutral-600">{center.module_count} modules</span>
                      <button
                        type="button"
                        onClick={() => toggleAdministrators(center.id)}
                        className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 px-3 py-2 text-xs font-semibold text-neutral-700 transition hover:bg-neutral-50"
                        aria-expanded={expanded}
                      >
                        <UsersThree size={15} weight="duotone" />
                        {center.administrator_count} admins
                        <CaretDown size={13} weight="bold" className={expanded ? "rotate-180 transition-transform" : "transition-transform"} />
                      </button>
                    </div>
                  </div>
                  {expanded ? (
                    <div className="mt-4 rounded-2xl bg-[#F8F7F4] p-4">
                      {adminLoading[center.id] ? <p className="text-sm text-neutral-500">Loading administrators…</p> : (
                        <>
                          <ul className="space-y-2">
                            {rows.length ? rows.map((administrator) => (
                              <li key={administrator.profile_id} className="flex items-center gap-3 rounded-xl bg-white px-3 py-2.5">
                                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary-hover"><CheckCircle size={16} weight="fill" /></span>
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-semibold text-neutral-800">{administrator.full_name || "Administrator"}</p>
                                  <p className="truncate text-xs text-neutral-500">{administrator.email || "Account assigned"}</p>
                                </div>
                              </li>
                            )) : <li className="py-2 text-sm text-neutral-500">No administrator is assigned yet.</li>}
                          </ul>
                          {isOwner ? (
                            <form className="mt-4 grid gap-2 border-t border-neutral-200 pt-4 sm:grid-cols-[1fr_1fr_auto]" onSubmit={(event) => assignAdministrator(event, center.id)}>
                              <input aria-label="Administrator email" required type="email" placeholder="administrator@example.com" value={form.email} onChange={(event) => setAdminForms((previous) => ({ ...previous, [center.id]: { ...form, email: event.target.value } }))} className={inputClass} />
                              <input aria-label="Administrator name" placeholder="Full name (optional)" value={form.full_name} onChange={(event) => setAdminForms((previous) => ({ ...previous, [center.id]: { ...form, full_name: event.target.value } }))} className={inputClass} />
                              <button disabled={adminSaving[center.id]} className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-neutral-900 px-3 py-2 text-xs font-semibold text-white transition hover:bg-neutral-800 disabled:cursor-wait disabled:opacity-60">
                                <UserPlus size={15} weight="bold" />
                                {adminSaving[center.id] ? "Assigning…" : "Assign admin"}
                              </button>
                            </form>
                          ) : null}
                        </>
                      )}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="rounded-3xl border border-neutral-200 bg-white p-5 shadow-card">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">Recent control-plane activity</p>
            <h2 className="mt-1 font-heading text-xl font-semibold tracking-tight text-neutral-900">Audit trail</h2>
          </div>
          <span className="text-xs text-neutral-400">Metadata only</span>
        </div>
        <ul className="mt-4 divide-y divide-neutral-100">
          {audit.length ? audit.slice(0, 8).map((entry) => (
            <li key={entry.id} className="flex items-center justify-between gap-4 py-3 text-sm">
              <span className="font-medium text-neutral-800">{humanizeAction(entry.action)}</span>
              <span className="shrink-0 text-xs text-neutral-500">{formatDate(entry.created_at)}</span>
            </li>
          )) : <li className="py-4 text-sm text-neutral-500">No platform actions recorded yet.</li>}
        </ul>
      </section>
    </div>
  );
}

export function PlatformLoadError({ onRetry }) {
  return (
    <section
      role="alert"
      data-testid="platform-control-error"
      className="mx-auto max-w-2xl rounded-3xl border border-amber-200 bg-white p-6 text-center shadow-card sm:p-8"
    >
      <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-50 text-amber-700">
        <WarningCircle size={25} weight="duotone" aria-hidden="true" />
      </span>
      <p className="mt-5 text-xs font-bold uppercase tracking-[0.15em] text-amber-700">Connection interrupted</p>
      <h1 className="mt-2 font-heading text-2xl font-semibold tracking-tight text-neutral-900">
        We couldn&apos;t load your centers
      </h1>
      <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-neutral-600">
        Your center setup has not been changed. Check your connection and try again.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-neutral-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:ring-offset-2"
      >
        <ArrowsClockwise size={17} weight="bold" aria-hidden="true" />
        Try again
      </button>
    </section>
  );
}

const inputClass = "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20";

function Metric({ label, value, icon }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm">
      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary-hover">{icon}</span>
      <p className="mt-4 text-xs font-bold uppercase tracking-[0.13em] text-neutral-500">{label}</p>
      <p className="mt-1 font-heading text-2xl font-semibold tracking-tight text-neutral-900">{value}</p>
    </div>
  );
}

function TextField({ label, value, onChange, hint, type = "text", required = false }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">{label}</span>
      <input type={type} required={required} value={value} onChange={(event) => onChange(event.target.value)} className={inputClass} />
      {hint ? <span className="mt-1 block text-[10px] text-neutral-400">{hint}</span> : null}
    </label>
  );
}

function humanizeAction(value) {
  return String(value || "platform_action").replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDate(value) {
  if (!value) return "Just now";
  try {
    return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(value));
  } catch {
    return "Recently";
  }
}
